import { createAsyncThunk } from '@reduxjs/toolkit';
import apiConfig from 'constants/apiConfig';
import { fetchCollaborationDeletionRequests, showCollaborationDeletionRequest } from 'services/api/collaboration';
import { ERROR } from 'types';
import {
    CollabDeletionRequest,
    CollabDeletionRequestDetail,
    CollabDeletionRequestsFilters,
    CollabDeletionRequestsSlice,
} from 'types/collaboration-deletion-request';
import { generateUniqueKey, getErrorMessage, isFresh } from 'utils/helpers';

export const DELETION_REQUEST_FILTER_KEY_EXCLUDES = ['page', 'campaign'];

export const buildDeletionRequestParams = (filters: CollabDeletionRequestsFilters) => {
    return Object.fromEntries(
        Object.entries(filters).filter(
            ([key, value]) =>
                key !== 'campaign' &&
                value !== '' &&
                value !== undefined &&
                value !== null
        )
    );
};

export const fetchCollaborationDeletionRequestsThunk = createAsyncThunk<
    {
        count: number;
        items: CollabDeletionRequest[];
        filters: CollabDeletionRequestsFilters;
        filterId: string;
    },
    { filters: CollabDeletionRequestsFilters; force?: boolean },
    { rejectValue: string }
>(
    'collaborationDeletionRequests/list',
    async ({ filters, force }, { getState, rejectWithValue }) => {
        try {
            const STALE_TIME = apiConfig.FETCH_COLLAB_DELETION_REQUESTS.staleTime;
            const filterId = generateUniqueKey(filters, DELETION_REQUEST_FILTER_KEY_EXCLUDES);
            const state = getState() as { collaborationDeletionRequests: CollabDeletionRequestsSlice };
            const cachedList = state.collaborationDeletionRequests?.filtered_lists?.[filterId];

            if (
                filters.page === 1 &&
                !force &&
                cachedList &&
                !cachedList.expired &&
                isFresh(cachedList.last_fetched_at, STALE_TIME)
            ) {
                return rejectWithValue(ERROR.CACHED);
            }

            const { count, items = [] } = await fetchCollaborationDeletionRequests(
                buildDeletionRequestParams(filters)
            );

            return { count, items, filters, filterId };
        } catch (error) {
            return rejectWithValue(
                getErrorMessage(error, 'Failed to fetch deletion requests')
            );
        }
    }
);

export const getDeletionRequestDetailCacheKey = (
    campaignId: string,
    collaborationId: string,
    requestId: string | number
) => `${campaignId}:${collaborationId}:${requestId}`;

export const fetchCollaborationDeletionRequestThunk = createAsyncThunk<
    {
        cacheKey: string;
        item: CollabDeletionRequestDetail;
    },
    {
        campaignId: string;
        collaborationId: string;
        requestId: string | number;
        force?: boolean;
    },
    { rejectValue: string }
>(
    'collaborationDeletionRequests/show',
    async ({ campaignId, collaborationId, requestId, force }, { getState, rejectWithValue }) => {
        try {
            const STALE_TIME = apiConfig.SHOW_COLLAB_DELETION_REQUEST.staleTime;
            const cacheKey = getDeletionRequestDetailCacheKey(campaignId, collaborationId, requestId);
            const state = getState() as { collaborationDeletionRequests: CollabDeletionRequestsSlice };
            const cached = state.collaborationDeletionRequests.details[cacheKey];

            if (!force && cached && !cached.expired && isFresh(cached.last_fetched_at, STALE_TIME)) {
                return rejectWithValue(ERROR.CACHED);
            }

            const { item } = await showCollaborationDeletionRequest(campaignId, collaborationId, requestId);
            if (!item) {
                throw new Error('Failed to fetch deletion request.');
            }
            return { cacheKey, item };
        } catch (error) {
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch deletion request.'));
        }
    }
);
